import React, { useState, useEffect, useCallback } from "react";
import { OverlayTrigger, Tooltip, Row, Col, Container, Card, CardBody, Table, Modal, Button, Form } from "react-bootstrap";
import {
    getReSupplySummary, saveReSummary, getReSupplySummaryBasinOptions,
} from "../../Services/dashboard";
import { updateReSupplyRequest, getRequestOverView } from "../../Services/resupply.js";

import s3sLogo from '../../assets/images/s3sLogo.png';
import { ToastContainer, toast } from "react-toastify";
import FullScreenLoader from "../../Component/Loader";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router";
import Header from "../../Component/Header";
import storageIcon from '../../assets/images/storage-web-icon.svg';
import notesWebIcon from '../../assets/images/note-web-icon.svg';
import checkCircleIcon from '../../assets/images/check-circle-icon.svg';
import deployIcon from '../../assets/images/deploy-icon.svg';
import resupplyIcon from '../../assets/images/resupply.svg';
import trangleExclamtionIcon from '../../assets/images/exclamation-icon-red.svg';
import circleExclamtionIcon from '../../assets/images/exclamation-icon-circle-org.svg';
import checkDeployIcon from '../../assets/images/check-deployed-icon.svg';
import "./style.css";
import { useDispatch } from "react-redux";
import { setRequestType } from "../../Redux/Slice/requestTypeSlice";

function ViewSummary() {
    let navigate = useNavigate();
    const dispatch = useDispatch();
    const { deal_id, job_id, country, request_id } = useParams();
    const [role, setRole] = useState(
        (typeof localStorage !== 'undefined' ? localStorage.getItem('userRole') : 'field tech')?.toLowerCase().trim() || 'field tech'
    );
    const isSupplyChain = role === 'supply chain';

    const [showLoader, setShowLoader] = useState(false);
    const [summaryData, setSummaryData] = useState({});
    const [assetData, setAssetData] = useState(false);
    const [isDeployEnabled, setIsDeployEnabled] = useState(false);
    const [selectedBasin, setSelectedBasin] = useState({
        basin: "All",
        isDeployed: false,
        deployedDate: ""
    });
    const [basinOptions, setBasinOptions] = useState([]);

    // Approval modal states
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [approvalNotes, setApprovalNotes] = useState('');
    const [isApproving, setIsApproving] = useState(false);

    const [reRequestDetails, setReRequestDetails] = useState({
        requested: false,
        Needed_On: "",
        Expected_Delivery_Date: "",
        delivery_date: "",
        Status: null,
        priority: "",
        comments: null,
        requestedBy: {
            FirstName: "",
            LastName: ""
        }
    });

    // --------------------------------------------------------------
    // Helper: Check if a row is valid for loading (has supplied by & asset numbers)
    // --------------------------------------------------------------
    const isRowValid = (row) => {
        if (!row) return false;

        // For Equipment rows: need SuppliedBy and AssetDetails
        if (row.SuppliedBy !== undefined && row.AssetDetails !== undefined) {
            const hasSuppliedBy = row.SuppliedBy && Array.isArray(row.SuppliedBy) &&
                row.SuppliedBy.some(s => s && s.trim() !== '');
            const hasAssetNumbers = row.AssetDetails && Array.isArray(row.AssetDetails) &&
                row.AssetDetails.some(details =>
                    details && Array.isArray(details) &&
                    details.some(a => a && a.AssetNo && a.AssetNo.trim() !== '')
                );
            return hasSuppliedBy && hasAssetNumbers;
        }

        // For all other rows: check SuppliedBy field
        return row.SuppliedBy && row.SuppliedBy.trim() !== '';
    };

    // --------------------------------------------------------------
    // Helper: Get checkbox state (disabled & checked) for a row
    // --------------------------------------------------------------
    const getCheckboxState = (item) => {
        const valid = isRowValid(item);
        const isDeployed = item?.IsDeployed === true;
        const disabled = !valid || isDeployed;
        const checked = (item?.Loaded && valid) || isDeployed;
        return { disabled, checked };
    };

    useEffect(() => {
        if (job_id) {
            fetchBasin()
            fetchRequestOverView()
        }
    }, [job_id]);

    useEffect(() => {
        if (job_id) {
            fetchSummary(job_id)
        }
    }, [job_id, selectedBasin]);

    // Function to check if all "Ready To Ship" checkboxes are checked
    const checkAllReadyToShip = (data) => {
        if (!data || !data.summary) return false;
        const categories = [
            'Equipment',
            'Hose',
            'Items',
            'LabEquipment',
            'HSE',
            '3rdPartyEquipment',
            'CustomPackageItem'
        ];

        const checkArrayItems = (array) => {
            if (!Array.isArray(array) || array.length === 0) return true;
            return array.every(item => item.Loaded === true);
        };

        const checkItemsCategory = (itemsData) => {
            if (!itemsData || !Array.isArray(itemsData)) return true;
            const allItems = [];
            itemsData.forEach(category => {
                if (category && typeof category === 'object') {
                    if (category.items && Array.isArray(category.items)) {
                        allItems.push(...category.items);
                    }
                    if (category.SubCategory && Array.isArray(category.SubCategory)) {
                        category.SubCategory.forEach(subCategory => {
                            if (subCategory.items && Array.isArray(subCategory.items)) {
                                allItems.push(...subCategory.items);
                            }
                        });
                    }
                }
            });
            if (allItems.length === 0) return true;
            return allItems.every(item => item.Loaded === true);
        };

        for (const category of categories) {
            if (category === 'Items') {
                if (!checkItemsCategory(data.summary[category])) {
                    return false;
                }
            } else {
                if (!checkArrayItems(data.summary[category])) {
                    return false;
                }
            }
        }
        return true;
    };

    useEffect(() => {
        if (summaryData && Object.keys(summaryData).length > 0) {
            const isEnabled = checkAllReadyToShip(summaryData);
            setIsDeployEnabled(isEnabled);
        }
    }, [summaryData]);

    const fetchRequestOverView = useCallback(async () => {
        const payload = { Request_ID: request_id, JobDetails_ID: job_id };
        const res = await getRequestOverView(localStorage.access_token, payload);
        if (res && res?.data?.status == 200) {
            const data = res.data.data;
            setReRequestDetails(prev => ({
                ...prev,
                Needed_On: data?.Needed_On ? data.Needed_On.split(" ")[0] : "",
                priority: data?.Priority,
                Expected_Delivery_Date: data?.Expected_Delivery_Date ? data.Expected_Delivery_Date.split("T")[0] : "",
                requestedBy: data?.Requested_User_Profile,
                Status: data?.Status,
                Comments: data?.Comments
            }));
        }
    }, [request_id, deal_id]);

    let fetchBasin = async () => {
        let res = await getReSupplySummaryBasinOptions(
            localStorage.access_token,
            job_id,
            country,
            'Resupply',
            request_id
        );
        if (res?.data?.result) {
            const withAllOption = [
                {
                    basin: "All",
                    isDeployed: false,
                    deployedDate: ""
                },
                ...res.data.result
            ];
            setBasinOptions(withAllOption);
            return withAllOption;
        }
        return null;
    };

    // --- Helper to set all Loaded = true (only for valid rows, deployed forced) ---
    const setAllLoadedTrue = (summary) => {
        if (!summary) return summary;
        const newSummary = { ...summary };
        const categories = ['Equipment', 'Hose', 'Items', 'LabEquipment', 'HSE', '3rdPartyEquipment', 'CustomPackageItem'];
        categories.forEach(cat => {
            if (cat === 'Items') {
                if (newSummary[cat] && Array.isArray(newSummary[cat])) {
                    newSummary[cat] = newSummary[cat].map(category => {
                        const newCategory = { ...category };
                        if (newCategory.items && Array.isArray(newCategory.items)) {
                            newCategory.items = newCategory.items.map(item => {
                                if (item?.IsDeployed === true) {
                                    return { ...item, Loaded: true };
                                }
                                const valid = isRowValid(item);
                                return { ...item, Loaded: valid };
                            });
                        }
                        if (newCategory.SubCategory && Array.isArray(newCategory.SubCategory)) {
                            newCategory.SubCategory = newCategory.SubCategory.map(sub => {
                                const newSub = { ...sub };
                                if (newSub.items && Array.isArray(newSub.items)) {
                                    newSub.items = newSub.items.map(item => {
                                        if (item?.IsDeployed === true) {
                                            return { ...item, Loaded: true };
                                        }
                                        const valid = isRowValid(item);
                                        return { ...item, Loaded: valid };
                                    });
                                }
                                return newSub;
                            });
                        }
                        return newCategory;
                    });
                }
            } else {
                if (newSummary[cat] && Array.isArray(newSummary[cat])) {
                    newSummary[cat] = newSummary[cat].map(item => {
                        if (item?.IsDeployed === true) {
                            return { ...item, Loaded: true };
                        }
                        const valid = isRowValid(item);
                        return { ...item, Loaded: valid };
                    });
                }
            }
        });
        return newSummary;
    };

    let fetchSummary = async (id) => {
        try {
            setShowLoader(true);
            let res = await getReSupplySummary(localStorage?.access_token, id, selectedBasin.basin, 'Resupply', request_id);
            setShowLoader(false);
            if (res.status === 200) {
                if (res.data?.status_code === 200) {
                    const result = res.data?.result;
                    if (result) {
                        // Auto-check all checkboxes (only for valid rows)
                        const summaryWithLoaded = setAllLoadedTrue(result.summary);
                        setSummaryData({
                            ...result,
                            summary: summaryWithLoaded
                        });
                    }
                }
                const result = hasSummaryData(res?.data?.result?.summary);
                setAssetData(result)
            }
        } catch (err) {
            console.error("Error fetching hoses:", err);
            setShowLoader(false);
        }
    }

    function hasSummaryData(summary) {
        return Object.values(summary).some(
            arr => Array.isArray(arr) && arr.length > 0
        );
    }

    const updateCheckboxState = (updatedData) => {
        setSummaryData(updatedData);
    };

    // Handler for Equipment
    const handleEquipmentCheckboxChange = (categoryIndex, field, checked) => {
        setSummaryData(prevData => {
            const updatedSummary = { ...prevData.summary };
            const updatedEquipment = [...updatedSummary.Equipment];
            const row = updatedEquipment[categoryIndex];
            // Only allow change if row is valid AND not deployed
            if (!isRowValid(row) || row?.IsDeployed === true) return prevData;
            updatedEquipment[categoryIndex] = {
                ...row,
                [field]: checked
            };
            updatedSummary.Equipment = updatedEquipment;
            const updatedData = {
                ...prevData,
                summary: updatedSummary
            };
            updateCheckboxState(updatedData);
            return updatedData;
        });
    };

    const handleHoseCheckboxChange = (hoseIndex, field, checked) => {
        setSummaryData(prevData => {
            const updatedSummary = { ...prevData.summary };
            const updatedHose = [...updatedSummary.Hose];
            const row = updatedHose[hoseIndex];
            if (!isRowValid(row) || row?.IsDeployed === true) return prevData;
            updatedHose[hoseIndex] = {
                ...row,
                [field]: checked
            };
            updatedSummary.Hose = updatedHose;
            const updatedData = {
                ...prevData,
                summary: updatedSummary
            };
            updateCheckboxState(updatedData);
            return updatedData;
        });
    };

    const handleItemsCheckboxChange = (categoryIndex, itemIndex, field, checked, hasSubCategory = false, subCategoryIndex = null) => {
        setSummaryData(prevData => {
            const updatedSummary = { ...prevData.summary };
            const updatedItems = [...updatedSummary.Items];
            let row;
            if (hasSubCategory && subCategoryIndex !== null) {
                const updatedSubCategory = [...updatedItems[categoryIndex].SubCategory];
                row = updatedSubCategory[subCategoryIndex].items[itemIndex];
                if (!isRowValid(row) || row?.IsDeployed === true) return prevData;
                const updatedSubItems = [...updatedSubCategory[subCategoryIndex].items];
                updatedSubItems[itemIndex] = {
                    ...row,
                    [field]: checked
                };
                updatedSubCategory[subCategoryIndex] = {
                    ...updatedSubCategory[subCategoryIndex],
                    items: updatedSubItems
                };
                updatedItems[categoryIndex] = {
                    ...updatedItems[categoryIndex],
                    SubCategory: updatedSubCategory
                };
            } else {
                row = updatedItems[categoryIndex].items[itemIndex];
                if (!isRowValid(row) || row?.IsDeployed === true) return prevData;
                const updatedCategoryItems = [...updatedItems[categoryIndex].items];
                updatedCategoryItems[itemIndex] = {
                    ...row,
                    [field]: checked
                };
                updatedItems[categoryIndex] = {
                    ...updatedItems[categoryIndex],
                    items: updatedCategoryItems
                };
            }
            updatedSummary.Items = updatedItems;
            const updatedData = {
                ...prevData,
                summary: updatedSummary
            };
            updateCheckboxState(updatedData);
            return updatedData;
        });
    };

    const handleLabEquipmentCheckboxChange = (labIndex, field, checked) => {
        setSummaryData(prevData => {
            const updatedSummary = { ...prevData.summary };
            const updatedLabEquipment = [...updatedSummary.LabEquipment];
            const row = updatedLabEquipment[labIndex];
            if (!isRowValid(row) || row?.IsDeployed === true) return prevData;
            updatedLabEquipment[labIndex] = {
                ...row,
                [field]: checked
            };
            updatedSummary.LabEquipment = updatedLabEquipment;
            const updatedData = {
                ...prevData,
                summary: updatedSummary
            };
            updateCheckboxState(updatedData);
            return updatedData;
        });
    };

    const handleHSECheckboxChange = (hseIndex, field, checked) => {
        setSummaryData(prevData => {
            const updatedSummary = { ...prevData.summary };
            const updatedHSE = [...updatedSummary.HSE];
            const row = updatedHSE[hseIndex];
            if (!isRowValid(row) || row?.IsDeployed === true) return prevData;
            updatedHSE[hseIndex] = {
                ...row,
                [field]: checked
            };
            updatedSummary.HSE = updatedHSE;
            const updatedData = {
                ...prevData,
                summary: updatedSummary
            };
            updateCheckboxState(updatedData);
            return updatedData;
        });
    };

    const handleThirdPartyCheckboxChange = (thirdPartyIndex, field, checked) => {
        setSummaryData(prevData => {
            const updatedSummary = { ...prevData.summary };
            const updatedThirdParty = [...updatedSummary['3rdPartyEquipment']];
            const row = updatedThirdParty[thirdPartyIndex];
            if (!isRowValid(row) || row?.IsDeployed === true) return prevData;
            updatedThirdParty[thirdPartyIndex] = {
                ...row,
                [field]: checked
            };
            updatedSummary['3rdPartyEquipment'] = updatedThirdParty;
            const updatedData = {
                ...prevData,
                summary: updatedSummary
            };
            updateCheckboxState(updatedData);
            return updatedData;
        });
    };

    const handleCustomPackageCheckboxChange = (customIndex, field, checked) => {
        setSummaryData(prevData => {
            const updatedSummary = { ...prevData.summary };
            const updatedCustomPackage = [...updatedSummary.CustomPackageItem];
            const row = updatedCustomPackage[customIndex];
            if (!isRowValid(row) || row?.IsDeployed === true) return prevData;
            updatedCustomPackage[customIndex] = {
                ...row,
                [field]: checked
            };
            updatedSummary.CustomPackageItem = updatedCustomPackage;
            const updatedData = {
                ...prevData,
                summary: updatedSummary
            };
            updateCheckboxState(updatedData);
            return updatedData;
        });
    };

    // --------------------------------------------------------------
    // convertToPayload – filters only selected (Loaded) items when deploying
    // --------------------------------------------------------------
    function convertToPayload(data, type) {
        if (!data || typeof data !== 'object') {
            throw new Error('Invalid data provided');
        }

        const isDeploy = type === 'deploy';
        // Filter: only include items that are Loaded AND NOT already deployed AND valid
        const filterLoaded = (item) => {
            // Only consider valid items for loading
            const valid = isRowValid(item);
            return valid && (!isDeploy || (item.Loaded === true && item.IsDeployed !== true));
        };

        const createSummaryArray = (sourceArray, idKey = 'ID') => {
            if (!sourceArray || !Array.isArray(sourceArray)) return [];
            return sourceArray
                .filter(filterLoaded)
                .map(item => ({
                    [idKey]: item[idKey] || item.EquipmentID || 0,
                    Loaded: Boolean(item.Loaded),
                    Recieved: Boolean(item.Recieved),
                    isDeployed: isDeploy ? true : Boolean(item.isDeployed)
                }));
        };

        const flattenItems = (itemsData) => {
            if (!itemsData || !Array.isArray(itemsData)) return [];
            const allItems = [];
            const processItemsArray = (itemsArray) => {
                if (!itemsArray || !Array.isArray(itemsArray)) return;
                itemsArray.forEach(item => {
                    if (item && typeof item === 'object' && filterLoaded(item)) {
                        allItems.push({
                            ID: item.ID || 0,
                            Loaded: Boolean(item.Loaded),
                            Recieved: Boolean(item.Recieved)
                        });
                    }
                });
            };
            itemsData.forEach(category => {
                if (category && typeof category === 'object') {
                    if (category.items && Array.isArray(category.items)) {
                        processItemsArray(category.items);
                    }
                    if (category.SubCategory && Array.isArray(category.SubCategory)) {
                        category.SubCategory.forEach(subCategory => {
                            if (subCategory.items && Array.isArray(subCategory.items)) {
                                processItemsArray(subCategory.items);
                            }
                        });
                    }
                }
            });
            return allItems;
        };

        return {
            summary: {
                Equipment: createSummaryArray(data.Equipment, 'EquipmentID'),
                Hose: createSummaryArray(data.Hose),
                Items: flattenItems(data.Items),
                CustomPackageItem: createSummaryArray(data.CustomPackageItem),
                LabEquipment: createSummaryArray(data.LabEquipment),
                HSE: createSummaryArray(data.HSE),
                ThirdPartyEquipment: createSummaryArray(data["3rdPartyEquipment"]),
                deployed: {
                    basin: selectedBasin.basin,
                    isDeployed: true,
                    deployedDate: new Date().toISOString().slice(0, 19).replace('T', ' ')
                }
            }
        };
    }

    let handleSave = async (type) => {
        const result = convertToPayload(summaryData?.summary, type);
        setShowLoader(true);
        const getCountry = country === 'USA' ? 'USA' : 'Canada';
        let res = await saveReSummary(
            localStorage.access_token,
            result,
            getCountry,
            job_id,
            type === 'deploy',
            'Resupply',
            request_id
        );

        if (res && res.status === 200 && res.data?.status_code === 200) {
            toast.success(res.data?.message || "Equipment details saved successfully!");

            // Refresh summary and basin options
            await fetchSummary(job_id);
            const newOptions = await fetchBasin();

            if (selectedBasin.basin !== "All" && newOptions) {
                const updatedBasin = newOptions.find(
                    b => b.basin === selectedBasin.basin
                );
                if (updatedBasin) {
                    setSelectedBasin(updatedBasin);
                }
            }
        } else {
            toast.error("Failed to save ❌");
        }
        setShowLoader(false);
    };

    const handleDeploy = async () => {
        await handleSave('deploy');
    }

    let handleBsinFilterClick = (val) => {
        setSelectedBasin(val)
    }

    const getStatusClass = (status) => {
        switch (status) {
            case false:
                return 'btn-inprogress-txt';
            case true:
                return 'btn-deployed-txt';
            default:
                return 'btn-inprogress-txt';
        }
    }

    const checkAllItemsDeployed = (summary) => {
        if (!summary) return false;

        const categories = ['Equipment', 'Hose', 'Items', 'LabEquipment', 'HSE', '3rdPartyEquipment', 'CustomPackageItem'];

        for (const cat of categories) {
            const items = summary[cat];
            if (!items || !Array.isArray(items)) continue;

            if (cat === 'Items') {
                for (const category of items) {
                    if (category.items && Array.isArray(category.items)) {
                        for (const item of category.items) {
                            if (item.IsDeployed !== true) return false;
                        }
                    }
                    if (category.SubCategory && Array.isArray(category.SubCategory)) {
                        for (const sub of category.SubCategory) {
                            if (sub.items && Array.isArray(sub.items)) {
                                for (const item of sub.items) {
                                    if (item.IsDeployed !== true) return false;
                                }
                            }
                        }
                    }
                }
            } else {
                for (const item of items) {
                    if (item.IsDeployed !== true) return false;
                }
            }
        }

        return true;
    };

    function areAllBasinsDeployed(data) {
        return data
            .filter(item => item.basin !== "All")
            .every(item => item.isDeployed === true);
    }

    const handlePrint = () => {
        window.print();
    };

    let handleResupplyClick = () => {
        dispatch(setRequestType({ RequestType: 'Resupply' }));
        localStorage.setItem('requestType', 'Resupply')
        navigate(`/home/${deal_id}/${country}?requestType=Resupply`)
    }

    const getStatusColor = (status) => {
        if (typeof status === "number") return
        if (!status) return { backgroundColor: "#e9ecef", color: "#495057" };
        const s = status.toLowerCase();
        if (s === "not started")
            return { backgroundColor: "#e9ecef", color: "#495057" };
        if (s === "completed")
            return { backgroundColor: "#d4edda", color: "#155724" };
        if (s === "in progress")
            return { backgroundColor: "#fff3cd", color: "#856404" };
        if (s === "request raised")
            return { backgroundColor: "#cce5ff", color: "#004085" };
        if (s === "approved")
            return { backgroundColor: "#d1ecf1", color: "#0c5460" };
        if (s === "rejected")
            return { backgroundColor: "#f8d7da", color: "#721c24" };
        if (s === "dispatched")
            return { backgroundColor: "#ffe5b4", color: "#8a6d3b" };
        if (s === "resupply completed")
            return { backgroundColor: "#b7e4c7", color: "#1e6f3f" };
        if (s === "deployed")
            return { backgroundColor: "#b7e4c7", color: "#1e6f3f" };
        return { backgroundColor: "#e9ecef", color: "#495057" };
    };

    // --- Approve handler ---
    const handleApprove = () => {
        setShowApproveModal(true);
        setApprovalNotes('');
    };

    const handleApproveClose = () => {
        setShowApproveModal(false);
        setApprovalNotes('');
    };


    const handleApproveSubmit = async () => {
        let statusId = 5
        try {
            setIsApproving(true);
            setShowLoader(true);
            const payload = {
                "JobDetails_ID": job_id,
                "Request_ID": request_id,
                "Needed_On": reRequestDetails.Needed_On,
                "Status": statusId,
                "priority": reRequestDetails?.priority,
                "comments": approvalNotes,
                "Expected_Delivery_Date": reRequestDetails?.Expected_Delivery_Date ?? ""
            };
            const res = await updateReSupplyRequest(
                payload,
                localStorage.access_token,
            );
            setShowLoader(false);
            setIsApproving(false);
            if (res && res.status === 200 && res.data?.data?.status_code === 200) {
                toast.success(res.data?.message || "Request approved successfully!");
                handleApproveClose();
                fetchSummary(job_id);
                fetchBasin();
                fetchRequestOverView()
            } else {
                toast.error(res?.data?.data?.message || "Approval failed ❌");
            }
        } catch (error) {
            console.error("Approval error:", error);
            setShowLoader(false);
            setIsApproving(false);
            toast.error("An error occurred during approval");
        }
    };


    return (
        <>
            <div className="wrapper">
                {showLoader ? <FullScreenLoader></FullScreenLoader> : null}
                <Header />
                <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    pauseOnHover
                    draggable
                    theme="colored"
                />
                <div className="full-assets-mange">
                    <Container fluid>
                        <Row>
                            <Col xs={12}>
                                <div className="d-flex align-items-start mb-3">
                                    {/* LEFT: BREADCRUMB */}
                                    <div className="flex-shrink-0">
                                        <ul className="d-flex align-items-center breadcum-menu-pg mb-0">
                                            <li className="list-breadcum-mn">
                                                <button
                                                    type="button"
                                                    className="btn p-0 border-0 itm-txtwgt-breadcum"
                                                    onClick={() => navigate(`/home/${job_id}/${request_id}/${deal_id}/${country}`)}
                                                >
                                                    Resupply form
                                                </button>
                                            </li>
                                            <li className="list-breadcum-mn">
                                                <span className="itm-txtwgt-breadcum">
                                                    Resupply Summary
                                                </span>
                                            </li>
                                        </ul>
                                    </div>

                                    {/* MIDDLE: BASINS */}
                                    <div className="flex-grow-1 d-flex justify-content px-3">
                                        <div className="d-flex flex-wrap gap-2">
                                            {basinOptions.map((val, i) => (
                                                <div
                                                    key={i}
                                                    className="d-flex flex-column align-items-center"
                                                >
                                                    <button
                                                        onClick={() => handleBsinFilterClick(val)}
                                                        type="button"
                                                        className={`btn btn-web-deploy-filter ${selectedBasin.basin === val.basin ? "active" : val.isDeployed ? "deployed" : ""
                                                            }`}
                                                    >
                                                        {val.basin}
                                                    </button>
                                                    {val.basin !== "All" && (
                                                        <p className="basin-date">{val.deployedDate ? new Date(val.deployedDate).toLocaleDateString("en-GB").replace(/\//g, "-")
                                                            : ''}</p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* RIGHT: APPROVE / DEPLOY / STATUS */}
                                    <div
                                        className="flex-shrink-0 d-flex align-items-center"
                                        style={{ gap: "5px" }}
                                    >
                                        {
                                            console.log("role ->", role, summaryData?.Status)
                                        }
                                        {
                                            (role === "field leader" || role === "area manager") && summaryData?.Status === "Request Raised"
                                                ? (
                                                    <button onClick={handleApprove} type="button" className="btn btn-web-resupply">
                                                        Approve
                                                    </button>
                                                )
                                                : (role === "supply chain" && (summaryData?.Status === "Approved" || summaryData?.Status === "Partial Deployed"))
                                                    ? (
                                                        <>
                                                            <button
                                                                onClick={handleDeploy}
                                                                disabled={
                                                                    selectedBasin.basin === "All"
                                                                        ? checkAllItemsDeployed(summaryData?.summary)
                                                                        : selectedBasin.isDeployed
                                                                }
                                                                type="button"
                                                                className="btn btn-web-deploy"
                                                            >
                                                                <img src={deployIcon} className="me-2" alt="" />
                                                                Deploy
                                                            </button>
                                                        </>
                                                    )
                                                    : (
                                                        <button type="button" className="btn btn-inprgres-wp" style={getStatusColor(summaryData?.Status)}>
                                                            {summaryData?.Status}
                                                        </button>
                                                    )
                                        }
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} md={12} lg={3} className="col-grid-web-2 pe-0">
                                <div className="side-card-summry mb-3 mb-md-5">
                                    <Card className="card-boxdtl mb-3">
                                        <CardBody className="px-2">
                                            <div className="d-flex align-items-center justify-content-between headline-wapflx pb-3">
                                                <h3 className="title-head-wp mb-0">Job Details</h3>
                                                <div className="ps-2">
                                                    <span
                                                        style={{ cursor: 'pointer' }}
                                                        className="txt-btn-proty-deploy"
                                                        onClick={handlePrint}
                                                    >
                                                        <img src={checkDeployIcon} className="me-1" alt="" />Print
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="multi-list-dtlasts">
                                                {
                                                    reRequestDetails?.Comments ?
                                                        <div className="list-grid--itm">
                                                            <h5 className="lst-ttl-mnt">Note</h5>
                                                            <h3 className="wgt-title-pt">{reRequestDetails?.Comments}</h3>
                                                        </div>
                                                        :
                                                        null
                                                }

                                                <div className="list-grid--itm">
                                                    <h5 className="lst-ttl-mnt">Job Name</h5>
                                                    <h3 className="wgt-title-pt">{summaryData?.jobDetails?.[0]?.JobName}</h3>
                                                </div>
                                                <div className="list-grid--itm">
                                                    <h5 className="lst-ttl-mnt">CUSTOMER NAME</h5>
                                                    <h3 className="wgt-title-pt">{summaryData?.jobDetails?.[0]?.CustomerName}</h3>
                                                </div>
                                                <div className="list-grid--itm">
                                                    <h5 className="lst-ttl-mnt">Rig/Platform Name </h5>
                                                    <h3 className="wgt-title-pt">{summaryData?.jobDetails?.[0]?.PlatformName}</h3>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                    <Card className="card-boxdtl">
                                        <CardBody className="px-2">
                                            <div className="headline-wapflx pb-3">
                                                <h3 className="title-head-wp mb-0">General Information</h3>
                                            </div>
                                            <div className="multi-list-dtlasts">
                                                <div className="list-grid--itm">
                                                    <h5 className="lst-ttl-mnt">Customer PO Number</h5>
                                                    <h3 className="wgt-title-pt">{ }</h3>
                                                </div>
                                                <div className="list-grid--itm">
                                                    <h5 className="lst-ttl-mnt">Internal Job ID</h5>
                                                    <h3 className="wgt-title-pt">{summaryData?.generalInformation?.[0]?.ID}</h3>
                                                </div>
                                                <div className="list-grid--itm">
                                                    <h5 className="lst-ttl-mnt">Area Manager</h5>
                                                    <h3 className="wgt-title-pt">{summaryData?.generalInformation?.[0]?.AreaManager}</h3>
                                                </div>
                                                <div className="list-grid--itm">
                                                    <h5 className="lst-ttl-mnt">Field Leader</h5>
                                                    <h3 className="wgt-title-pt">{summaryData?.generalInformation?.[0]?.FieldLeader}</h3>
                                                </div>
                                                <div className="list-grid--itm">
                                                    <h5 className="lst-ttl-mnt">Primary Basin</h5>
                                                    <h3 className="wgt-title-pt">{summaryData?.generalInformation?.[0]?.PrimaryBasin}</h3>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>
                            </Col>
                            <Col xs={12} md={12} lg={9} className="col-grid-web-1" id="print-div">
                                <Card className="card-sumry-waps mb-5">
                                    <CardBody className="px-0 card-bd-scroll">
                                        <div className="assets-summry-wgt">
                                            {summaryData?.summary?.Equipment?.length ?
                                                <>
                                                    <div className="multi-category-chartlist">
                                                        <div className="flx-category-emt">
                                                            <div className="flx-icon-wtm"><img src={storageIcon} alt="" /></div>
                                                            <div className="info-wpl-info">
                                                                <span className="small-tl-sp">category</span>
                                                                <h3 className="title-mgt--wp">EQUIPMENT</h3>
                                                            </div>
                                                        </div>
                                                        <div className="assets-table-sumry px-4">
                                                            <Table responsive className="table-summary-elemnt">
                                                                <thead className="thead-web-sumr">
                                                                    <tr>
                                                                        <th>Sls Asset Type</th>
                                                                        <th>Requested Qty</th>
                                                                        <th>Ops Asset Type</th>
                                                                        <th>Approved Qty</th>
                                                                        <th>Spare / Non Billable</th>
                                                                        <th>Supplied Qty</th>
                                                                        <th>
                                                                            <div style={{ display: "flex", gap: "20px", fontWeight: 600 }}>
                                                                                <div style={{ width: "120px" }}>Supplied By</div>
                                                                                <div style={{ width: "140px" }}>ASSET NUMBERS</div>
                                                                            </div>
                                                                        </th>
                                                                        <th>Availability Date</th>
                                                                        <th>Status</th>
                                                                        <th></th>
                                                                        {isSupplyChain &&
                                                                            <th>
                                                                            </th>}
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="tbody-area-lyt">
                                                                    {summaryData?.summary?.Equipment?.map((eq, i) => {
                                                                        const { disabled, checked } = getCheckboxState(eq);
                                                                        return (
                                                                            <tr key={i} className={eq?.IsDeployed ? 'deployed-row' : ''}>
                                                                                <td>{eq?.AssetType}</td>
                                                                                <td>{eq?.RS_RequestedQuantity}</td>
                                                                                <td>{eq?.SupplyChainAssetType}</td>
                                                                                <td>{eq?.RS_ApprovedQuantity}</td>
                                                                                <td>
                                                                                    <div className={eq?.RS_ApprovedQuantity > eq?.RS_RequestedQuantity ? `spare-box-field summry-txt-discweb` : `spare-box-field-disabled`}>
                                                                                        <div className="input-wave--asts mb-2">
                                                                                            <span className="text-spare-label light-blue-spare">
                                                                                                <div className="dot-circle-spare"></div>Spare
                                                                                            </span>
                                                                                            <div className="spare-bill-ast">
                                                                                                <input type="number" disabled value={eq?.SpareCount} className="form-control input-bill-ast" />
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="input-wave--asts">
                                                                                            <span className="text-spare-label light-green-spare">
                                                                                                <div className="dot-circle-spare"></div>Non Billable
                                                                                            </span>
                                                                                            <div className="spare-bill-ast">
                                                                                                <input type="number" disabled value={eq?.NonBillableCount} className="form-control input-bill-ast" />
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td>{eq?.RS_SuppliedQuantity}</td>
                                                                                <td>
                                                                                    <div style={{ display: "flex", gap: "20px" }}>
                                                                                        <div>
                                                                                            {Array.from({ length: Math.max(eq?.SuppliedBy?.length || 0, eq?.AssetDetails?.length || 0) }).map((_, index) => (
                                                                                                <div key={index} style={{ display: "flex", gap: "20px", marginBottom: "6px" }}>
                                                                                                    <div style={{ width: "120px" }}>{eq?.SuppliedBy?.[index] || ""}</div>
                                                                                                    <div style={{ width: "140px" }}>
                                                                                                        {eq?.AssetDetails?.[index]?.map((ele, i) => (
                                                                                                            <div key={i}>{ele.AssetNo}</div>
                                                                                                        )) || ""}
                                                                                                    </div>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    {
                                                                                        eq?.RS_ExpectedAvailableDeliveryDate ?
                                                                                            new Date(eq?.RS_ExpectedAvailableDeliveryDate).toLocaleDateString('en-GB')
                                                                                            :
                                                                                            'n/a'
                                                                                    }
                                                                                </td>

                                                                                <td>
                                                                                    <span className={`btn-status-prgs ${getStatusClass(eq?.IsDeployed)} text-nowrap`}>
                                                                                        {
                                                                                            eq?.Status
                                                                                        }
                                                                                    </span>
                                                                                </td>
                                                                                <td>
                                                                                    <OverlayTrigger
                                                                                        placement="top"
                                                                                        overlay={<Tooltip id={eq?.Notes}>{eq?.Notes ? `${eq?.Notes}` : "n/a"}</Tooltip>}
                                                                                    >
                                                                                        <span style={{ cursor: "pointer" }} className="note-icon-block">
                                                                                            <img style={{ minWidth: '20px' }} src={notesWebIcon} alt="" />
                                                                                        </span>
                                                                                    </OverlayTrigger>
                                                                                </td>
                                                                                {isSupplyChain && (
                                                                                    <td>
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={checked}
                                                                                            disabled={disabled}
                                                                                            onChange={(e) => handleEquipmentCheckboxChange(i, 'Loaded', e.target.checked)}
                                                                                            style={{
                                                                                                width: '22px',
                                                                                                height: '22px',
                                                                                                cursor: disabled ? 'not-allowed' : 'pointer',
                                                                                                accentColor: '#007bff',
                                                                                                margin: '4px 0'
                                                                                            }}
                                                                                        />
                                                                                    </td>
                                                                                )}
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </div>
                                                </>
                                                : null
                                            }
                                            {summaryData?.summary?.Hose?.length ?
                                                <>
                                                    <div className="multi-category-chartlist">
                                                        <div className="flx-category-emt">
                                                            <div className="flx-icon-wtm"><img src={storageIcon} alt="" /></div>
                                                            <div className="info-wpl-info">
                                                                <span className="small-tl-sp">Category</span>
                                                                <h3 className="title-mgt--wp">HOSE</h3>
                                                            </div>
                                                        </div>
                                                        <div className="assets-table-sumry px-4">
                                                            <Table responsive className="table-summary-elemnt">
                                                                <thead className="thead-web-sumr">
                                                                    <tr>
                                                                        <th>Hose Type</th>
                                                                        <th>HOSE DIAMETER</th>
                                                                        <th>SECTION LENGTH</th>
                                                                        <th>END FITTING TYPE 1</th>
                                                                        <th>END FITTING TYPE 2</th>
                                                                        <th>Qty</th>
                                                                        <th>Supplied By</th>
                                                                        <th>Status</th>
                                                                        {isSupplyChain && <th>
                                                                            
                                                                            </th>}
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="tbody-area-lyt">
                                                                    {summaryData?.summary?.Hose?.map((hse, i) => {
                                                                        const { disabled, checked } = getCheckboxState(hse);
                                                                        return (
                                                                            <tr key={i} className={hse?.IsDeployed ? 'deployed-row' : ''}>
                                                                                <td>{hse?.HoseType}</td>
                                                                                <td>{hse?.HoseDiameter}</td>
                                                                                <td>{hse?.SectionLength}</td>
                                                                                <td>{hse?.EndFittingType1}</td>
                                                                                <td>{hse?.EndFittingType2}</td>
                                                                                <td>{hse?.Quantity}</td>
                                                                                <td>{hse?.SuppliedBy}</td>
                                                                                <td>
                                                                                    <span className={`btn-status-prgs ${getStatusClass(hse?.IsDeployed)} text-nowrap`}>
                                                                                        {
                                                                                            hse?.Status
                                                                                        }
                                                                                    </span>
                                                                                </td>
                                                                                {isSupplyChain && (
                                                                                    <td>
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={checked}
                                                                                            disabled={disabled}
                                                                                            onChange={(e) => handleHoseCheckboxChange(i, 'Loaded', e.target.checked)}
                                                                                            style={{
                                                                                                width: '22px',
                                                                                                height: '22px',
                                                                                                cursor: disabled ? 'not-allowed' : 'pointer',
                                                                                                accentColor: '#007bff',
                                                                                                margin: '4px 0'
                                                                                            }}
                                                                                        />
                                                                                    </td>
                                                                                )}
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </div>
                                                </>
                                                : null
                                            }
                                            {summaryData?.summary?.Items?.length ?
                                                <>
                                                    <div className="multi-category-chartlist">
                                                        <div className="flx-category-emt">
                                                            <div className="flx-icon-wtm"><img src={storageIcon} alt="" /></div>
                                                            <div className="info-wpl-info">
                                                                <span className="small-tl-sp">Category</span>
                                                                <h3 className="title-mgt--wp">ITEMS</h3>
                                                            </div>
                                                        </div>
                                                        {summaryData?.summary?.Items?.map((category, index) => (
                                                            <div key={index} className="assets-table-sumry px-4">
                                                                <h3 className="title-hs-mt">{category.MasterCategory}</h3>
                                                                {category.SubCategory && category.SubCategory.length > 0 ? (
                                                                    category.SubCategory.map((subCategory, subIndex) => (
                                                                        <div key={subIndex}>
                                                                            <h5 className="title-hs-mt">{subCategory.MasterCategory}</h5>
                                                                            <Table responsive className="table-summary-elemnt">
                                                                                <thead className="thead-web-sumr">
                                                                                    <tr>
                                                                                        <th>Item Name</th>
                                                                                        <th>Qty</th>
                                                                                        <th>Supplied By</th>
                                                                                        <th>Status</th>
                                                                                        {isSupplyChain && <th></th>}
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody className="tbody-area-lyt">
                                                                                    {subCategory.items.map((item, itemIndex) => {
                                                                                        const { disabled, checked } = getCheckboxState(item);
                                                                                        return (
                                                                                            <tr key={itemIndex} className={item?.IsDeployed ? 'deployed-row' : ''}>
                                                                                                <td className="item-head-wp-1">{item.ItemName}</td>
                                                                                                <td className="item-head-wp-2">{item.Quantity}</td>
                                                                                                <td className="item-head-wp-3">{item.SuppliedBy}</td>
                                                                                                <td>
                                                                                                    <span className={`btn-status-prgs ${getStatusClass(item?.IsDeployed)} text-nowrap`}>
                                                                                                        {
                                                                                                            item?.Status
                                                                                                        }
                                                                                                    </span>
                                                                                                </td>
                                                                                                {isSupplyChain && (
                                                                                                    <td>
                                                                                                        <input
                                                                                                            type="checkbox"
                                                                                                            checked={checked}
                                                                                                            disabled={disabled}
                                                                                                            onChange={(e) => handleItemsCheckboxChange(index, itemIndex, 'Loaded', e.target.checked, true, subIndex)}
                                                                                                            style={{
                                                                                                                width: '22px',
                                                                                                                height: '22px',
                                                                                                                cursor: disabled ? 'not-allowed' : 'pointer',
                                                                                                                accentColor: '#007bff',
                                                                                                                margin: '4px 0'
                                                                                                            }}
                                                                                                        />
                                                                                                    </td>
                                                                                                )}
                                                                                            </tr>
                                                                                        );
                                                                                    })}
                                                                                </tbody>
                                                                            </Table>
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    <Table responsive className="table-summary-elemnt">
                                                                        <thead className="thead-web-sumr">
                                                                            <tr>
                                                                                <th>Item Name</th>
                                                                                <th>Qty</th>
                                                                                <th>Supplied By</th>
                                                                                <th>Status</th>
                                                                                {isSupplyChain && <th></th>}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className="tbody-area-lyt">
                                                                            {category.items.map((item, itemIndex) => {
                                                                                const { disabled, checked } = getCheckboxState(item);
                                                                                return (
                                                                                    <tr key={itemIndex} className={item?.IsDeployed ? 'deployed-row' : ''}>
                                                                                        <td className="item-head-wp-1">{item.ItemName}</td>
                                                                                        <td className="item-head-wp-2">{item.Quantity}</td>
                                                                                        <td className="item-head-wp-3">{item.SuppliedBy}</td>
                                                                                        <td>
                                                                                            <span className={`btn-status-prgs ${getStatusClass(item?.IsDeployed)} text-nowrap`}>
                                                                                                {
                                                                                                    item?.Status
                                                                                                }
                                                                                            </span>
                                                                                        </td>
                                                                                        {isSupplyChain && (
                                                                                            <td>
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    checked={checked}
                                                                                                    disabled={disabled}
                                                                                                    onChange={(e) => handleItemsCheckboxChange(index, itemIndex, 'Loaded', e.target.checked, false, null)}
                                                                                                    style={{
                                                                                                        width: '22px',
                                                                                                        height: '22px',
                                                                                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                                                                                        accentColor: '#007bff',
                                                                                                        margin: '4px 0'
                                                                                                    }}
                                                                                                />
                                                                                            </td>
                                                                                        )}
                                                                                    </tr>
                                                                                );
                                                                            })}
                                                                        </tbody>
                                                                    </Table>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                                : null
                                            }
                                            {summaryData?.summary?.LabEquipment?.length ?
                                                <>
                                                    <div className="multi-category-chartlist">
                                                        <div className="flx-category-emt">
                                                            <div className="flx-icon-wtm"><img src={storageIcon} alt="" /></div>
                                                            <div className="info-wpl-info">
                                                                <span className="small-tl-sp">Category</span>
                                                                <h3 className="title-mgt--wp">Lab Items</h3>
                                                            </div>
                                                        </div>
                                                        <div className="assets-table-sumry px-4">
                                                            <Table responsive className="table-summary-elemnt">
                                                                <thead className="thead-web-sumr">
                                                                    <tr>
                                                                        <th>Item Name</th>
                                                                        <th>Qty</th>
                                                                        <th>Supplied By</th>
                                                                        <th>Status</th>
                                                                        {isSupplyChain && <th></th>}
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="tbody-area-lyt">
                                                                    {summaryData?.summary?.LabEquipment?.map((lab, i) => {
                                                                        const { disabled, checked } = getCheckboxState(lab);
                                                                        return (
                                                                            <tr key={i} className={lab?.IsDeployed ? 'deployed-row' : ''}>
                                                                                <td className="item-head-wp-1">{lab.ItemName}</td>
                                                                                <td className="item-head-wp-2">{lab.Quantity}</td>
                                                                                <td className="item-head-wp-3">{lab.SuppliedBy}</td>
                                                                                <td>
                                                                                    <span className={`btn-status-prgs ${getStatusClass(lab?.IsDeployed)} text-nowrap`}>
                                                                                        {
                                                                                            lab?.Status
                                                                                        }
                                                                                    </span>
                                                                                </td>
                                                                                {isSupplyChain && (
                                                                                    <td>
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={checked}
                                                                                            disabled={disabled}
                                                                                            onChange={(e) => handleLabEquipmentCheckboxChange(i, 'Loaded', e.target.checked)}
                                                                                            style={{
                                                                                                width: '22px',
                                                                                                height: '22px',
                                                                                                cursor: disabled ? 'not-allowed' : 'pointer',
                                                                                                accentColor: '#007bff',
                                                                                                margin: '4px 0'
                                                                                            }}
                                                                                        />
                                                                                    </td>
                                                                                )}
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </div>
                                                </>
                                                : null
                                            }
                                            {summaryData?.summary?.HSE?.length ?
                                                <>
                                                    <div className="multi-category-chartlist">
                                                        <div className="flx-category-emt">
                                                            <div className="flx-icon-wtm"><img src={storageIcon} alt="" /></div>
                                                            <div className="info-wpl-info">
                                                                <span className="small-tl-sp">Category</span>
                                                                <h3 className="title-mgt--wp">HSE</h3>
                                                            </div>
                                                        </div>
                                                        <div className="assets-table-sumry px-4">
                                                            <Table responsive className="table-summary-elemnt">
                                                                <thead className="thead-web-sumr">
                                                                    <tr>
                                                                        <th>Item Name</th>
                                                                        <th>Qty</th>
                                                                        <th>Supplied By</th>
                                                                        <th>Status</th>
                                                                        <th></th>
                                                                        {isSupplyChain && <th></th>}
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="tbody-area-lyt">
                                                                    {summaryData?.summary?.HSE?.map((ele, i) => {
                                                                        const { disabled, checked } = getCheckboxState(ele);
                                                                        return (
                                                                            <tr key={i} className={ele?.IsDeployed ? 'deployed-row' : ''}>
                                                                                <td className="item-head-wp-1">{ele.ItemName}</td>
                                                                                <td className="item-head-wp-2">{ele.Quantity}</td>
                                                                                <td className="item-head-wp-3">{ele.SuppliedBy}</td>
                                                                                <td>
                                                                                    <span className={`btn-status-prgs ${getStatusClass(ele?.IsDeployed)} text-nowrap`}>
                                                                                        {
                                                                                            ele?.Status
                                                                                        }
                                                                                    </span>
                                                                                </td>
                                                                                <td className="item-head-wp-6">
                                                                                    <OverlayTrigger
                                                                                        placement="top"
                                                                                        overlay={<Tooltip id={ele?.Notes}>{ele?.Notes ? `${ele?.Notes}` : 'n/a'}</Tooltip>}
                                                                                    >
                                                                                        <span style={{ cursor: 'pointer' }} className="note-icon-block"><img style={{ minWidth: '20px' }} src={notesWebIcon} alt="" /></span>
                                                                                    </OverlayTrigger>
                                                                                </td>
                                                                                {isSupplyChain && (
                                                                                    <td>
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={checked}
                                                                                            disabled={disabled}
                                                                                            onChange={(e) => handleHSECheckboxChange(i, 'Loaded', e.target.checked)}
                                                                                            style={{
                                                                                                width: '22px',
                                                                                                height: '22px',
                                                                                                cursor: disabled ? 'not-allowed' : 'pointer',
                                                                                                accentColor: '#007bff',
                                                                                                margin: '4px 0'
                                                                                            }}
                                                                                        />
                                                                                    </td>
                                                                                )}
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </div>
                                                </>
                                                : null
                                            }
                                            {summaryData?.summary?.CustomPackageItem?.length ?
                                                <>
                                                    <div className="multi-category-chartlist">
                                                        <div className="flx-category-emt">
                                                            <div className="flx-icon-wtm"><img src={storageIcon} alt="" /></div>
                                                            <div className="info-wpl-info">
                                                                <span className="small-tl-sp">Category</span>
                                                                <h3 className="title-mgt--wp">Custom Items</h3>
                                                            </div>
                                                        </div>
                                                        <div className="assets-table-sumry px-4">
                                                            <Table responsive className="table-summary-elemnt">
                                                                <thead className="thead-web-sumr">
                                                                    <tr>
                                                                        <th>Item Name</th>
                                                                        <th>Qty</th>
                                                                        <th>Supplied By</th>
                                                                        <th>Status</th>
                                                                        <th></th>
                                                                        {isSupplyChain && <th></th>}
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="tbody-area-lyt">
                                                                    {summaryData?.summary['CustomPackageItem']?.map((ele, i) => {
                                                                        const { disabled, checked } = getCheckboxState(ele);
                                                                        return (
                                                                            <tr key={i} className={ele?.IsDeployed ? 'deployed-row' : ''}>
                                                                                <td className="item-head-wp-1">{ele.ItemName}</td>
                                                                                <td className="item-head-wp-2">{ele.Quantity}</td>
                                                                                <td className="item-head-wp-3">{ele.SuppliedBy}</td>
                                                                                <td>
                                                                                    <span className={`btn-status-prgs ${getStatusClass(ele?.IsDeployed)} text-nowrap`}>
                                                                                        {
                                                                                            ele?.Status
                                                                                        }
                                                                                    </span>
                                                                                </td>
                                                                                <td className="item-head-wp-6">
                                                                                    <OverlayTrigger
                                                                                        placement="top"
                                                                                        overlay={<Tooltip id={ele?.Notes}>{ele?.Notes ? `${ele?.Notes}` : 'n/a'}</Tooltip>}
                                                                                    >
                                                                                        <span style={{ cursor: 'pointer' }} className="note-icon-block"><img style={{ minWidth: '20px' }} src={notesWebIcon} alt="" /></span>
                                                                                    </OverlayTrigger>
                                                                                </td>
                                                                                {isSupplyChain && (
                                                                                    <td>
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={checked}
                                                                                            disabled={disabled}
                                                                                            onChange={(e) => handleCustomPackageCheckboxChange(i, 'Loaded', e.target.checked)}
                                                                                            style={{
                                                                                                width: '22px',
                                                                                                height: '22px',
                                                                                                cursor: disabled ? 'not-allowed' : 'pointer',
                                                                                                accentColor: '#007bff',
                                                                                                margin: '4px 0'
                                                                                            }}
                                                                                        />
                                                                                    </td>
                                                                                )}
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </div>
                                                </>
                                                : null
                                            }
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </div>

                {/* APPROVAL MODAL */}
                <Modal
                    show={showApproveModal}
                    onHide={handleApproveClose}
                    centered
                    size="md"
                    className="approve-modal"
                >
                    <Modal.Header closeButton className="border-0 pb-0">
                        <div className="d-flex align-items-center gap-3">
                            <div className="approve-icon-wrapper">
                                <svg
                                    width="40"
                                    height="40"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle cx="12" cy="12" r="10" fill="#2B7A4B" opacity="0.15" />
                                    <path
                                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                                        fill="#2B7A4B"
                                    />
                                </svg>
                            </div>
                            <div>
                                <Modal.Title className="fw-bold fs-4">Confirm Approval</Modal.Title>
                                <p className="text-muted small mb-0">Approve this resupply request</p>
                            </div>
                        </div>
                    </Modal.Header>

                    <Modal.Body className="pt-2">
                        <Form>
                            <Form.Group controlId="approvalNotes">
                                <Form.Label className="fw-semibold">
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        className="me-1"
                                    >
                                        <path
                                            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
                                            fill="#6C757D"
                                        />
                                    </svg>
                                    Approval Notes <span className="text-muted fw-light">(optional)</span>
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={approvalNotes}
                                    onChange={(e) => setApprovalNotes(e.target.value)}
                                    placeholder="Add any notes for this approval (e.g., reason, additional instructions)…"
                                    className="border-2 rounded-3 shadow-sm"
                                    style={{ resize: 'vertical' }}
                                />
                                <Form.Text className="text-muted">
                                    <small>This note will be visible to the supply chain team.</small>
                                </Form.Text>
                            </Form.Group>
                        </Form>
                    </Modal.Body>

                    <Modal.Footer className="border-0 pt-0">
                        <Button
                            variant="light"
                            onClick={handleApproveClose}
                            disabled={isApproving}
                            className="rounded-pill px-4"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="me-1">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="#6C757D" />
                            </svg>
                            Cancel
                        </Button>
                        <Button
                            variant="success"
                            onClick={handleApproveSubmit}
                            disabled={isApproving}
                            className="rounded-pill px-4"
                        >
                            {isApproving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Submitting…
                                </>
                            ) : (
                                <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="me-1">
                                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                                    </svg>
                                    Submit
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Print-only content - hidden on screen */}
                <div id="print-content" style={{ display: 'none' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ border: 'none', textAlign: 'left', padding: '10px 0' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                        <img src={s3sLogo} alt="S3S Logo" style={{ maxWidth: '100px', maxHeight: '50px' }} />
                                        <h2 style={{ margin: 0 }}>Resupply Summary</h2>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{ border: 'none', padding: '5px 0' }}>
                                    <div className="print-header-info">
                                        <p><strong>Job Name:</strong> {summaryData?.jobDetails?.[0]?.JobName}</p>
                                        <p><strong>Customer:</strong> {summaryData?.jobDetails?.[0]?.CustomerName}</p>
                                        <p><strong>Rig/Platform:</strong> {summaryData?.jobDetails?.[0]?.PlatformName}</p>
                                        <p><strong>Internal Job ID:</strong> {summaryData?.generalInformation?.[0]?.ID}</p>
                                        <p><strong>Primary Basin:</strong> {summaryData?.generalInformation?.[0]?.PrimaryBasin}</p>
                                    </div>
                                </td>
                            </tr>
                            {summaryData?.summary?.Equipment?.length > 0 && (
                                <tr>
                                    <td style={{ border: 'none', padding: '5px 0' }}>
                                        <div className="print-category">
                                            <h3>EQUIPMENT</h3>
                                            <table className="print-table equipment-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: '12%' }}>Sls Asset Type</th>
                                                        <th style={{ width: '6%' }}>Requested Qty</th>
                                                        <th style={{ width: '12%' }}>Ops Asset Type</th>
                                                        <th style={{ width: '6%' }}>Approved Qty</th>

                                                        <th style={{ width: '12%' }}>Spare/Non Bill</th>
                                                        <th style={{ width: '6%' }}>Supplied Qty</th>
                                                        <th style={{ width: '30%' }}>Supplied By / Asset Numbers</th>
                                                        <th style={{ width: '30%' }}>Availibility date</th>
                                                        <th style={{ width: '8%' }}>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {summaryData.summary.Equipment.map((eq, i) => (
                                                        <tr key={i}>
                                                            <td>{eq.AssetType}</td>
                                                            <td>{eq.RS_RequestedQuantity}</td>
                                                            <td>{eq.SupplyChainAssetType}</td>
                                                            <td>{eq.RS_ApprovedQuantity}</td>
                                                            <td>
                                                                {eq.RS_ApprovedQuantity > eq.RS_RequestedQuantity ? (
                                                                    <>
                                                                        <div>Spare: {eq.SpareCount}</div>
                                                                        <div>Non Bill: {eq.NonBillableCount}</div>
                                                                    </>
                                                                ) : '—'}
                                                            </td>
                                                            <td>{eq.RS_SuppliedQuantity}</td>
                                                            <td>
                                                                {eq.SuppliedBy?.map((supplier, idx) => (
                                                                    <div key={idx}>
                                                                        <strong>{supplier}:</strong>{' '}
                                                                        {eq.AssetDetails?.[idx]?.map(a => a.AssetNo).join(', ')}
                                                                    </div>
                                                                ))}
                                                            </td>
                                                            <td>
                                                                {
                                                                    eq?.RS_ExpectedAvailableDeliveryDate ?
                                                                        new Date(eq?.RS_ExpectedAvailableDeliveryDate).toLocaleDateString('en-GB')
                                                                        :
                                                                        'n/a'
                                                                }
                                                            </td>
                                                            <td>{eq.Status}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {summaryData?.summary?.Hose?.length > 0 && (
                                <tr>
                                    <td style={{ border: 'none', padding: '5px 0' }}>
                                        <div className="print-category">
                                            <h3>HOSE</h3>
                                            <table className="print-table">
                                                <thead>
                                                    <tr>
                                                        <th>Hose Type</th>
                                                        <th>Diameter</th>
                                                        <th>Length</th>
                                                        <th>End Fitting 1</th>
                                                        <th>End Fitting 2</th>
                                                        <th>Qty</th>
                                                        <th>Supplied By</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {summaryData.summary.Hose.map((hse, i) => (
                                                        <tr key={i}>
                                                            <td>{hse.HoseType}</td>
                                                            <td>{hse.HoseDiameter}</td>
                                                            <td>{hse.SectionLength}</td>
                                                            <td>{hse.EndFittingType1}</td>
                                                            <td>{hse.EndFittingType2}</td>
                                                            <td>{hse.Quantity}</td>
                                                            <td>{hse.SuppliedBy}</td>
                                                            <td>{hse.Status}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {summaryData?.summary?.Items?.length > 0 && (
                                <tr>
                                    <td style={{ border: 'none', padding: '5px 0' }}>
                                        <div className="print-category">
                                            <h3>ITEMS</h3>
                                            {summaryData.summary.Items.map((category, idx) => {
                                                if (category.SubCategory && category.SubCategory.length > 0) {
                                                    return (
                                                        <div key={idx} className="print-subcategory">
                                                            <h4>{category.MasterCategory}</h4>
                                                            {category.SubCategory.map((sub, subIdx) => (
                                                                <div key={subIdx}>
                                                                    <h5>{sub.MasterCategory}</h5>
                                                                    <table className="print-table">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Item Name</th>
                                                                                <th>Qty</th>
                                                                                <th>Supplied By</th>
                                                                                <th>Status</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {sub.items.map((item, itemIdx) => (
                                                                                <tr key={itemIdx}>
                                                                                    <td>{item.ItemName}</td>
                                                                                    <td>{item.Quantity}</td>
                                                                                    <td>{item.SuppliedBy}</td>
                                                                                    <td>{item.Status}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    );
                                                } else {
                                                    return (
                                                        <div key={idx}>
                                                            <h4>{category.MasterCategory}</h4>
                                                            <table className="print-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Item Name</th>
                                                                        <th>Qty</th>
                                                                        <th>Supplied By</th>
                                                                        <th>Status</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {category.items.map((item, itemIdx) => (
                                                                        <tr key={itemIdx}>
                                                                            <td>{item.ItemName}</td>
                                                                            <td>{item.Quantity}</td>
                                                                            <td>{item.SuppliedBy}</td>
                                                                            <td>{item.Status}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    );
                                                }
                                            })}
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {summaryData?.summary?.LabEquipment?.length > 0 && (
                                <tr>
                                    <td style={{ border: 'none', padding: '5px 0' }}>
                                        <div className="print-category">
                                            <h3>LAB ITEMS</h3>
                                            <table className="print-table">
                                                <thead>
                                                    <tr>
                                                        <th>Item Name</th>
                                                        <th>Qty</th>
                                                        <th>Supplied By</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {summaryData.summary.LabEquipment.map((lab, i) => (
                                                        <tr key={i}>
                                                            <td>{lab.ItemName}</td>
                                                            <td>{lab.Quantity}</td>
                                                            <td>{lab.SuppliedBy}</td>
                                                            <td>{lab.Status}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {summaryData?.summary?.HSE?.length > 0 && (
                                <tr>
                                    <td style={{ border: 'none', padding: '5px 0' }}>
                                        <div className="print-category">
                                            <h3>HSE</h3>
                                            <table className="print-table">
                                                <thead>
                                                    <tr>
                                                        <th>Item Name</th>
                                                        <th>Qty</th>
                                                        <th>Supplied By</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {summaryData.summary.HSE.map((hse, i) => (
                                                        <tr key={i}>
                                                            <td>{hse.ItemName}</td>
                                                            <td>{hse.Quantity}</td>
                                                            <td>{hse.SuppliedBy}</td>
                                                            <td>{hse.Status}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {summaryData?.summary?.CustomPackageItem?.length > 0 && (
                                <tr>
                                    <td style={{ border: 'none', padding: '5px 0' }}>
                                        <div className="print-category">
                                            <h3>CUSTOM ITEMS</h3>
                                            <table className="print-table">
                                                <thead>
                                                    <tr>
                                                        <th>Item Name</th>
                                                        <th>Qty</th>
                                                        <th>Supplied By</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {summaryData.summary.CustomPackageItem.map((cust, i) => (
                                                        <tr key={i}>
                                                            <td>{cust.ItemName}</td>
                                                            <td>{cust.Quantity}</td>
                                                            <td>{cust.SuppliedBy}</td>
                                                            <td>{cust.Status}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

export default ViewSummary;